import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './OrderDetails.css';

function OrderDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);

    // Check if user is a customer
    if (parsedUser.role !== 'customer') {
      navigate('/admin/dashboard');
      return;
    }

    setUser(parsedUser);
    fetchOrderDetails(id, parsedUser.email);
  }, [navigate, id]);

  const fetchOrderDetails = async (orderId, email) => {
    try {
      // Fetch order
      const orderResponse = await axios.get(`/api/orders/${orderId}`);
      const orderData = orderResponse.data;

      // Verify this order belongs to the logged-in user
      if (orderData.customer_email !== email) {
        navigate('/account/orders');
        return;
      }

      setOrder(orderData);

      // Fetch order items with product details
      const itemsResponse = await axios.get(`/api/orders/${orderId}/items`);
      setOrderItems(itemsResponse.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      if (error.response?.status === 404) {
        navigate('/account/orders');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="order-details">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details">
        <div className="error-message">Order not found</div>
      </div>
    );
  }

  const orderDate = new Date(order.created_at);
  const estimatedDelivery = new Date(orderDate);
  estimatedDelivery.setDate(estimatedDelivery.getDate() + 7); // Estimate 7 days for delivery

  return (
    <div className="order-details">
      <div className="account-container">
        <div className="account-sidebar">
          <div className="account-user-info">
            <div className="user-avatar">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <h3>{user?.name || 'Customer'}</h3>
            <p>{user?.email}</p>
          </div>

          <nav className="account-nav">
            <Link to="/account" className="account-nav-item">
              <span className="nav-icon">📊</span>
              Dashboard
            </Link>
            <Link to="/account/orders" className="account-nav-item active">
              <span className="nav-icon">📦</span>
              Order History
            </Link>
            <Link to="/account/addresses" className="account-nav-item">
              <span className="nav-icon">📍</span>
              Addresses
            </Link>
            <Link to="/account/wishlist" className="account-nav-item">
              <span className="nav-icon">❤️</span>
              Wishlist
            </Link>
            <Link to="/account/settings" className="account-nav-item">
              <span className="nav-icon">⚙️</span>
              Settings
            </Link>
            <button onClick={handleLogout} className="account-nav-item logout-btn">
              <span className="nav-icon">🚪</span>
              Logout
            </button>
          </nav>
        </div>

        <div className="account-main">
          <div className="breadcrumb">
            <Link to="/account">Account</Link>
            <span className="separator">›</span>
            <Link to="/account/orders">Orders</Link>
            <span className="separator">›</span>
            <span className="current">Order #{order.order_number}</span>
          </div>

          <div className="order-header">
            <div className="order-header-left">
              <h1>Order #{order.order_number}</h1>
              <p className="order-date">
                Placed on {orderDate.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <div className="order-header-right">
              <span className={`order-status-badge status-${order.status}`}>
                {order.status}
              </span>
            </div>
          </div>

          {/* Order Status Timeline */}
          <div className="status-timeline">
            <div className={`timeline-step ${['pending', 'processing', 'shipped', 'delivered'].includes(order.status) ? 'completed' : ''}`}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>Order Placed</h4>
                <p>{orderDate.toLocaleDateString()}</p>
              </div>
            </div>
            <div className={`timeline-step ${['processing', 'shipped', 'delivered'].includes(order.status) ? 'completed' : order.status === 'processing' ? 'active' : ''}`}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>Processing</h4>
                <p>Preparing your order</p>
              </div>
            </div>
            <div className={`timeline-step ${['shipped', 'delivered'].includes(order.status) ? 'completed' : order.status === 'shipped' ? 'active' : ''}`}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>Shipped</h4>
                <p>{order.tracking_number ? `Tracking: ${order.tracking_number}` : 'In transit'}</p>
              </div>
            </div>
            <div className={`timeline-step ${order.status === 'delivered' ? 'completed' : ''}`}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>Delivered</h4>
                <p>Est. {estimatedDelivery.toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="order-section">
            <h2>Order Items</h2>
            <div className="order-items-list">
              {orderItems.map((item, index) => (
                <div key={index} className="order-item">
                  <div className="item-image">
                    {item.product_image ? (
                      <img src={item.product_image} alt={item.product_name} />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </div>
                  <div className="item-details">
                    <h3>{item.product_name}</h3>
                    {item.variation_name && (
                      <p className="item-variation">{item.variation_name}</p>
                    )}
                    <p className="item-quantity">Quantity: {item.quantity}</p>
                  </div>
                  <div className="item-price">
                    <p className="price">${parseFloat(item.price).toFixed(2)}</p>
                    <p className="subtotal">
                      Subtotal: ${(parseFloat(item.price) * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="order-summary-section">
            <div className="order-info-grid">
              <div className="info-card">
                <h3>Shipping Address</h3>
                <p className="address-text">{order.shipping_address}</p>
              </div>

              <div className="info-card">
                <h3>Contact Information</h3>
                <p><strong>Name:</strong> {order.customer_name}</p>
                <p><strong>Email:</strong> {order.customer_email}</p>
                {order.customer_phone && (
                  <p><strong>Phone:</strong> {order.customer_phone}</p>
                )}
              </div>

              <div className="info-card order-total-card">
                <h3>Order Summary</h3>
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>${parseFloat(order.total).toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>FREE</span>
                </div>
                <div className="summary-row total-row">
                  <span>Total</span>
                  <span>${parseFloat(order.total).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="order-actions-section">
            <Link to="/account/orders" className="btn btn-secondary-outline">
              ← Back to Orders
            </Link>
            {order.tracking_number && (
              <a href={`#track-${order.tracking_number}`} className="btn btn-primary">
                Track Shipment
              </a>
            )}
            {order.status === 'pending' && (
              <button className="btn btn-danger">
                Cancel Order
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetails;
